import { useTranslation } from "next-i18next";
import React from "react";

import { UI_CONTEXTS } from "../../analytics/constants";
import EVENTS from "../../analytics/events";
import LinkButton from "../../components/buttons/link-button";
import { StoreProps } from "../../interfaces/stores-interface";
import { KINDS, SIZES } from "../buttons/buttons.constants";
import { ApLogoMini } from "../icons/ap-logo-mini";
import styles from "./mobileRetailerPage.module.scss";

type Props = {
    store: StoreProps;
};

export const MobileRetailerPage: React.FC<Props> = ({ store }) => {
    const { t } = useTranslation();

    const ButtonGroup = () => {
        return (
            <>
                {!store.inStoreOnly && (
                    <div className={styles.retailerPageButton}>
                        <LinkButton
                            disabled={false}
                            fluid={true}
                            kind={KINDS.PRIMARY}
                            label={t("retailerPage.buttonGroup.button1")}
                            size={SIZES.REGULAR}
                            show={true}
                            url={store.merchantOutboundUrl}
                            newTab={true}
                            analytics={{
                                eventName: EVENTS.BUTTON_CLICK,
                                uiContext: UI_CONTEXTS.RETAILER_PROFILE,
                            }}
                        />
                    </div>
                )}
                {store.hasLocations && (
                    <div className={styles.retailerPageButton}>
                        <LinkButton
                            disabled={false}
                            fluid={true}
                            kind={KINDS.PRIMARY}
                            label={t("retailerPage.buttonGroup.button2")}
                            size={SIZES.REGULAR}
                            show={true}
                            url={store.shopUrl}
                            newTab={true}
                            analytics={{
                                eventName: EVENTS.BUTTON_CLICK,
                                uiContext: UI_CONTEXTS.RETAILER_PROFILE,
                            }}
                        />
                    </div>
                )}
            </>
        );
    };

    const SignupBlurb = () => {
        return (
            <div className={styles.retailerPageSignupBlurb}>
                <div className={styles.logoContainer}>
                    <ApLogoMini />
                </div>
                <div className={styles.contentContainer}>
                    <h1 className={styles.contentHeading}>
                        {t("retailerPage.signupBlurb.heading")}
                    </h1>
                    <p className={styles.contentBody}>{t("retailerPage.signupBlurb.body")}</p>
                    <h2 className={styles.contentLink}>
                        {t("retailerPage.signupBlurb.contentLink")}
                    </h2>
                </div>
            </div>
        );
    };

    const TitleText = () => {
        return (
            <>
                <h1 className={styles.retailerPageTitle}>
                    {t("retailerPage.titleText.title", { merchantName: store.name })}
                </h1>
                <p className={styles.retailerPageTitleText}>
                    {t("retailerPage.titleText.text1", { merchantName: store.name })}
                </p>
                <p className={styles.retailerPageTitleText}>
                    {t("retailerPage.titleText.text2", { merchantName: store.name })}
                </p>
            </>
        );
    };

    const TextDetail = () => {
        return (
            <div className={styles.retailerTextDetail}>
                <div className={styles.textDetailSection}>
                    <h2 className={styles.textDetailHeading}>
                        {t("retailerPage.textDetail.section1.heading", {
                            merchantName: store.name,
                        })}
                    </h2>
                    <ul className={styles.textDetailList}>
                        <li className={styles.textDetailListItem}>
                            {t("retailerPage.textDetail.section1.listItem1")}
                        </li>
                        <li className={styles.textDetailListItem}>
                            {t("retailerPage.textDetail.section1.listItem2")}
                        </li>
                        <li className={styles.textDetailListItem}>
                            {t("retailerPage.textDetail.section1.listItem3", {
                                merchantName: store.name,
                            })}
                        </li>
                        <li className={styles.textDetailListItem}>
                            {t("retailerPage.textDetail.section1.listItem4")}
                        </li>
                    </ul>
                </div>
                {store.hasLocations && (
                    <div className={styles.textDetailSection}>
                        <h2 className={styles.textDetailHeading}>
                            {t("retailerPage.textDetail.section2.heading", {
                                merchantName: store.name,
                            })}
                        </h2>
                        <ul className={styles.textDetailList}>
                            <li className={styles.textDetailListItem}>
                                {t("retailerPage.textDetail.section2.listItem1")}
                            </li>
                            <li className={styles.textDetailListItem}>
                                {t("retailerPage.textDetail.section2.listItem2")}
                            </li>
                            <li className={styles.textDetailListItem}>
                                {t("retailerPage.textDetail.section2.listItem3")}
                            </li>
                            <li className={styles.textDetailListItem}>
                                {t("retailerPage.textDetail.section2.listItem4")}
                            </li>
                            <li className={styles.textDetailListItem}>
                                {t("retailerPage.textDetail.section2.listItem5")}
                            </li>
                        </ul>
                    </div>
                )}
            </div>
        );
    };

    const TextBlurb = () => {
        return (
            <div className={styles.retailerPagesTextBlurb}>
                <h1 className={styles.textBlurbHeading}>{t("retailerPage.textBlurb.heading")}</h1>
                <p className={styles.textBlurbBody}>{t("retailerPage.textBlurb.body")}</p>
            </div>
        );
    };

    return (
        <div className={styles.retailerPageContainer}>
            <TitleText />
            <ButtonGroup />
            <SignupBlurb />
            <TextDetail />
            <TextBlurb />
        </div>
    );
};
